import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_ORDERS } from '../../lib/mockData';
import OrderDetailView from './OrderDetailView';

const STATUS_LABELS = {
  pending:          'Order Received',
  paid:             'Payment Confirmed',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Hand Delivered',
  cancelled:        'Cancelled',
};

const STATUS_COLORS = {
  pending:          'bg-yellow-50 text-yellow-700 border-yellow-100',
  paid:             'bg-green-50 text-green-700 border-green-100',
  out_for_delivery: 'bg-teal-50 text-teal-700 border-teal-100',
  delivered:        'bg-sage-50 text-sage-700 border-sage-100',
  cancelled:        'bg-red-50 text-red-600 border-red-100',
};

function OrderCard({ order, onClick }) {
  const item = order.order_items?.[0] || {};
  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={() => onClick(order)}
      className="glass-panel p-6 cursor-pointer group transition-all"
    >
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="w-full sm:w-32 h-32 rounded-[2rem] overflow-hidden shrink-0 border border-sage-100 shadow-sm">
          <img
            src={item.design_image_url || order.design_image_url}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            alt="Design"
          />
        </div>
        <div className="flex-1 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-1">Order #{order.id.substring(0, 8)}</p>
              <h3 className="font-serif text-xl text-charcoal">{item.product_name || 'Custom Vase'}</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[order.status]}`}>
              {STATUS_LABELS[order.status]}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-sage-50">
            <div>
              <p className="text-[10px] text-sage-400 font-bold uppercase mb-0.5">Date</p>
              <p className="text-sm font-bold text-charcoal">{new Date(order.delivery_date).toLocaleDateString()}</p>
            </div>
            <div className="text-right col-span-1 md:col-start-4">
              <p className="text-[10px] text-sage-400 font-bold uppercase mb-0.5">Price</p>
              <p className="text-sm font-bold text-blush-500">${item.price || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function CustomerPortal() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userId, setUserId] = useState(null);

  const fetchOrders = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    if (isSupabaseConfigured()) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
          const { data } = await supabase
            .from('orders')
            .select('*, order_items(*), order_photos(*)')
            .eq('customer_id', user.id)
            .order('created_at', { ascending: false });
          if (data) {
            setOrders(data);
            setSelectedOrder(prev => prev ? data.find(o => o.id === prev.id) || prev : null);
          }
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setOrders(MOCK_ORDERS);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Set up realtime subscription once we have the userId
  // so we only react to this customer's order changes
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`portal-updates-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `customer_id=eq.${userId}`
        },
        () => fetchOrders(true)
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_photos'
        },
        () => fetchOrders(true)
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [userId]);

  if (loading) return (
    <div className="flex justify-center py-20 w-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300"></div>
    </div>
  );

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12">
      <AnimatePresence mode="wait">
        {selectedOrder ? (
          <OrderDetailView
            key="detail"
            order={selectedOrder}
            onBack={() => setSelectedOrder(null)}
            onUpdate={() => fetchOrders(true)}
          />
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-12"
          >
            <div className="space-y-2">
              <h1 className="text-4xl md:text-5xl font-serif text-charcoal">Order History</h1>
              <p className="text-sage-500 text-lg font-medium">Track your arrangements and delivery status.</p>
            </div>

            {orders.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {orders.map(order => <OrderCard key={order.id} order={order} onClick={setSelectedOrder} />)}
              </div>
            ) : (
              <div className="glass-panel p-20 text-center space-y-6">
                <SafeIcon name="ShoppingBag" className="text-6xl text-sage-100 mx-auto" />
                <h2 className="text-2xl font-serif text-charcoal">No orders found</h2>
                <Link to="/" className="btn-primary">Place Your First Order</Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
